import React, { useCallback, useEffect, useState, ReactNode, useContext } from "react";
import "./Sidebar-old.scss";
import { Icon, ThemedIcon } from "@me8eon/icons";
import { EnvironmentConfig } from "@me8eon/config";
import { AiAssistContext } from "me-at-eon/src/components/ai-assist-refactored/AiAssist.context";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "@me8eon/translation";
import { FocusTrap } from "@me8eon/focus_trap";
import { useUserData } from "@me8eon/react_authentication";
import { useWindowProperties } from "me-at-eon/src/context/WindowContext";
import { useAsyncSessionAndMessages, useSessionAndMessageOps } from "@me8eon/sessions_messages";
import { isDataAs, useQuestionSuggestions } from "@me8eon/async";
import { useFeatureFlag } from "@me8eon/featureflags";
import { useAuthenticatedFetch } from "@me8eon/new_ai_utils/src/hooks/useAuthenticatedFetch";
import { deleteAllConversations, listConversations } from "@me8eon/new_ai_utils/src/api/conversations";
import { CONVERSATION_UPDATED_EVENT, defaultChatConfig } from "@me8eon/new_ai_utils/src/config/chat.config";

interface SidebarProps {
    sidebar: ReactNode;
    children: ReactNode;
}

interface SidebarBtnProps {
    isExpanded: boolean;
    label: string;
    onClick?: () => void;
    noCursor?: boolean;
    icon: string;
    tooltip: string;
    deleteIcon?: boolean;
    tourId?: string;
}

const SidebarBtn = ({ isExpanded, label, onClick, icon, tooltip, noCursor, deleteIcon, tourId }: SidebarBtnProps) => {
    const translate = useTranslation();
    const { loggedIn } = useUserData();

    const [state] = useAsyncSessionAndMessages();
    const { deleteChild } = useSessionAndMessageOps();
    const authenticatedFetch = useAuthenticatedFetch();
    const [hasNewAiSessions, setHasNewAiSessions] = useState(false);

    const refreshHasNewAiSessions = useCallback(async () => {
        if (!deleteIcon || !loggedIn) {
            setHasNewAiSessions(false);
            return;
        }
        try {
            const payload = await listConversations(authenticatedFetch, defaultChatConfig.conversationsEndpoint, { limit: 1 });
            setHasNewAiSessions(payload.conversations.length > 0);
        } catch (error) {
            console.warn("Could not check for new-AI conversations; clear-all icon may be hidden.", error);
            setHasNewAiSessions(false);
        }
    }, [authenticatedFetch, deleteIcon, loggedIn]);

    useEffect(() => {
        void refreshHasNewAiSessions();
    }, [refreshHasNewAiSessions]);

    useEffect(() => {
        window.addEventListener(CONVERSATION_UPDATED_EVENT, refreshHasNewAiSessions);
        return () => window.removeEventListener(CONVERSATION_UPDATED_EVENT, refreshHasNewAiSessions);
    }, [refreshHasNewAiSessions]);

    // Legacy-only threshold (>2) is preserved as-is; new-AI conversations use their
    // own presence check since they aren't part of `parent.sessions` at all.
    const showDeleteIcon = (isDataAs(state) && state.data.parent.sessions.length > 2) || hasNewAiSessions;
    const [isDeletingAll, setIsDeletingAll] = useState(false);

    async function deleteAllSessions() {
        if (isDeletingAll) return;
        setIsDeletingAll(true);
        try {
            if (isDataAs(state)) {
                const ids = state.data.parent.sessions.map(session => session.id);
                if (ids.length > 0) await deleteChild(...ids);
            }
            await deleteAllConversations(authenticatedFetch, defaultChatConfig.conversationsEndpoint);
            window.dispatchEvent(new CustomEvent(CONVERSATION_UPDATED_EVENT));
        } catch (error) {
            console.error("Failed to delete new-AI conversations", error);
        } finally {
            setIsDeletingAll(false);
        }
    }

    return (
        <button className={`sidebar-btn ${noCursor ? "sidebar-btn--no-cursor" : ""}`} onClick={onClick} title={tooltip} id={tourId}>
            {icon === "newchat" ? <ThemedIcon name={icon} /> : <Icon name={icon} />}
            {isExpanded && (
                <div className="label-with-icon">
                    <span className={`sidebar-btn-label ${noCursor ? "sidebar-label--no-cursor" : ""}`}>{label}</span>
                    {showDeleteIcon && deleteIcon && (
                        <span
                            className="sidebar-btn-delete-icon"
                            title={translate("chatBot.sessions.clearAll")}
                            onClick={(event) => { event.stopPropagation(); void deleteAllSessions(); }}
                            aria-disabled={isDeletingAll}
                        >
                            {isDeletingAll
                                ? <span className="sidebar-btn-delete-icon-spinner" data-testid="sidebar.delete.all.spinner" aria-hidden="true" />
                                : <Icon name="trash_bin_small" style={{ width: "16px", height: "16px" }} />}
                        </span>
                    )}
                </div>
            )}
        </button>
    )
}

export const Sidebar = ({ sidebar, children }: SidebarProps) => {
    const { screenWidth } = useWindowProperties();
    const isMobile = screenWidth < 768;

    const [open, setOpen] = useState(!isMobile);
    const t = useTranslation();
    const navigate = useNavigate();
    const { handleResetChat, setCurrentAgent } = useContext(AiAssistContext);
    const { featureFlagEnabled } = useUserData();
    const [, setQs] = useQuestionSuggestions();
    const userData = useUserData();
    const loggedIn = userData.loggedIn;

    if (!loggedIn) {
        return children;
    }

    const handleClick = () => {
        window.location.href = `mailto:${EnvironmentConfig.meAtEonContactEmail}`;
    };

    const closeForMobile = () => {
        isMobile && setOpen(false);
    }

    const navigationClicked = (link: string) => {
        handleResetChat();
        closeForMobile();
        setCurrentAgent("");
        setQs([]);
        navigate(link);
    }

    return (
        <div className="sidebar-layout sidebar-layout-old">
            <FocusTrap>
                <aside
                    className={`sidebar ${open ? "open" : ""}`}
                >
                    <div className="sidebar-header">
                        <SidebarBtn
                            isExpanded={open}
                            label=""
                            onClick={() => setOpen(!open)}
                            icon={`${open ? "collapse" : "expand_sidebar"}`}
                            tooltip={`${open ? t("chatBot.close") : t("chatBot.open")}`}
                        />
                    </div>
                    <div className="sidebar-navigation">
                        <div id="tileTour5">
                            <SidebarBtn
                                isExpanded={open}
                                label={"Dashboard"}
                                icon="home"
                                tooltip={"Dashboard"}
                                onClick={() => navigationClicked("/tile")}
                                tourId="tileTour9"
                            />
                            {featureFlagEnabled && (<SidebarBtn
                                isExpanded={open}
                                label={t("chatBot.search")}
                                icon="search"
                                tooltip={t("chatBot.search")}
                                onClick={() => navigationClicked("/search-result")}
                            />)}
                            <div id="tileTour10">
                                <SidebarBtn
                                    isExpanded={open}
                                    label={t("chatBot.newChat")}
                                    icon="newchat"
                                    tooltip={t("chatBot.newChat")}
                                    onClick={() => navigationClicked("/ai")}
                                />
                                <SidebarBtn
                                    isExpanded={open}
                                    label={t("chatBot.recentChats")}
                                    onClick={() => setOpen(true)}
                                    icon={`recentChat`}
                                    tooltip={t("chatBot.recentChats")}
                                    noCursor={open}
                                    deleteIcon
                                />
                            </div>
                        </div>
                    </div>
                    <div className="sidebar-body">
                        {open ? sidebar : null}
                    </div>
                    <div className="sidebar-footer">
                        <SidebarBtn
                            isExpanded={open}
                            label={t("feedbackWidget")}
                            onClick={handleClick}
                            icon="feedback"
                            tooltip={t("feedbackWidget")}
                        />
                    </div>
                </aside>
            </FocusTrap>
            {open && (
                <div
                    id="sidebar-backdrop"
                    className="backdrop"
                    onClick={() => setOpen(false)}
                    data-testid="backdrop"
                />
            )}
            <div className={`main-content ${open ? "shifted" : ""}`}>
                <div className="me8eon-responsive-container">
                    {children}
                </div>
            </div>
        </div>
    );
}
